import { Seo } from "../components/Seo";
import { lazy, Suspense, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getJson } from "../lib/http";
import {
  ArrowRight,
  ShieldCheck,
  FlaskConical,
  Crown,
  Truck,
  Headphones,
} from "lucide-react";
import HeroWavingFlag from "../components/HeroWavingFlag";
import SafeImage from "../components/SafeImage";
import { STATIC_PRODUCTS, IMAGES, toAvif } from "../lib/products";
import PWAWelcomeReorder from "../components/PWAWelcomeReorder";
import ReviewHighlights from "../components/ReviewHighlights";
import staticContent from "../lib/staticContent.json";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FREE_SHIPPING_FALLBACK =
  Number(staticContent.free_shipping_threshold) > 0
    ? Number(staticContent.free_shipping_threshold)
    : 250;

// Hero markdown baked at build time by scripts/sync-static-catalog.js so the
// first render (and the SSR'd shell) already matches the live CMS content —
// the /api/pages/home fetch then usually resolves to the exact same text,
// which means zero post-load layout shift in the hero.
const HOME_HERO_FALLBACK =
  (staticContent.home_hero_md || "").trim() ||
  "## High Quality Retatrutide\n\n" +
    "Research-grade Retatrutide in multiple strengths — each batch " +
    "is independently tested by a Canadian testing lab.";

// Custom renderers for the hero markdown live in HeroMarkdown.jsx —
// lazy-loaded so react-markdown (~40 KB gz) stays out of the main bundle.
const HeroMarkdown = lazy(() => import("../components/HeroMarkdown"));

// Crude no-dependency renderer used as the Suspense fallback: handles the
// simple "## Heading" + paragraph shape the hero copy uses, with the same
// classes as HeroMarkdown so there's no visual jump when it swaps in.
function HeroPlain({ markdown }) {
  return (
    <>
      {markdown
        .split(/\n{2,}/)
        .map((block) => block.trim())
        .filter(Boolean)
        .map((block, i) =>
          /^#{1,3}\s/.test(block) ? (
            <h2
              key={i}
              className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight font-[700] leading-[0.95] animate-slide-up text-white"
            >
              {block.replace(/^#{1,3}\s+/, "")}
            </h2>
          ) : (
            <p
              key={i}
              className="mt-6 max-w-xl text-xl text-[#bfcde3] leading-relaxed animate-slide-up"
            >
              {block}
            </p>
          )
        )}
    </>
  );
}

// Split the CMS hero markdown into the title (first ##/### heading block)
// and everything else, so the badges + Browse-catalog button can sit right
// under the title regardless of what the admin writes in Pages → home.
function splitHeroMarkdown(md) {
  const blocks = (md || "")
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);
  const idx = blocks.findIndex((b) => /^#{1,3}\s/.test(b));
  if (idx === -1) return { title: "", body: blocks.join("\n\n") };
  return {
    title: blocks[idx],
    body: blocks.filter((_, j) => j !== idx).join("\n\n"),
  };
}

// The "Free shipping over $X" feature card is rebuilt at runtime so it
// reflects whatever the admin has currently set in Shipping Settings — same
// source of truth the disclaimer banner uses.
const buildFeatures = (freeShipping) => [
  {
    icon: Truck,
    title: "Xpresspost shipping",
    copy: "Canada-wide shiping. Next business day to 2 business day delivery.",
  },
  {
    icon: ShieldCheck,
    title: `Free shipping over $${
      Number.isInteger(freeShipping) ? freeShipping : freeShipping.toFixed(2)
    }`,
    copy: "Automatically applied at checkout.",
  },
  {
    icon: FlaskConical,
    title: "High purity, Canadian lab verified",
    copy: "Every lot is independently tested by Canadian laboratories.",
  },
  {
    icon: Headphones,
    title: "Human support",
    copy: "Rapid email responses from a human representative.",
  },
  {
    icon: Crown,
    title: "Attention to detail",
    copy: "Our meticulous attention to detail ensures every product is carefully inspected, accurately described, and thoughtfully packaged, so customers receive exactly what they expect—every time.",
  },
];

export default function Home() {
  const [products, setProducts] = useState(STATIC_PRODUCTS);
  const [freeShipping, setFreeShipping] = useState(FREE_SHIPPING_FALLBACK);
  const [heroMarkdown, setHeroMarkdown] = useState(HOME_HERO_FALLBACK);

  useEffect(() => {
    let active = true;
    getJson(`${API}/products`)
      .then((data) => {
        if (active && data?.products?.length) setProducts(data.products);
      })
      .catch(() => {});
    getJson(`${API}/shipping-info`)
      .then((data) => {
        const v = Number(data?.free_shipping_threshold);
        if (active && Number.isFinite(v) && v > 0) setFreeShipping(v);
      })
      .catch(() => {});
    getJson(`${API}/pages/home`)
      .then((data) => {
        const c = (data?.content || "").trim();
        if (active && c) setHeroMarkdown(c);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Prefer Retatrutide 20mg as the Hero headline product per design direction.
  const headline =
    products.find((p) => p.id === "reta-20mg") ||
    products[0] ||
    STATIC_PRODUCTS.find((p) => p.id === "reta-20mg") ||
    STATIC_PRODUCTS[0];
  const FEATURES = buildFeatures(freeShipping);

  return (
    <div data-testid="home-page" className="bg-[#0a0a0a]">
      <Seo
        title="Retatrutide Compound Specifications & Data"
        description="Pepura Labs provides high-purity (99%+) Retatrutide for in vitro research applications in Canada. Verified by independent Canadian laboratory COA documentation."
        path="/"
      />
      <PWAWelcomeReorder />
      <h1 data-testid="home-seo-h1" className="sr-only">
        Retatrutide Chemical Specifications &amp; Analytical Data
      </h1>
      {/* Hero */}
      <section className="hero-lines relative overflow-hidden border-b border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-14 sm:pt-24 pb-10 grid lg:grid-cols-12 gap-10 items-start relative">
          <div className="lg:col-span-7">
            <p className="section-tag animate-slide-up">
              Backed by science · Trusted by researchers
            </p>
            {(() => {
              const { title: heroTitle, body: heroBody } =
                splitHeroMarkdown(heroMarkdown);
              return (
                <>
                  <div data-testid="home-hero-markdown">
                    <Suspense fallback={<HeroPlain markdown={heroTitle} />}>
                      <HeroMarkdown markdown={heroTitle} />
                    </Suspense>
                  </div>

                  <div className="mt-6 flex flex-wrap items-center gap-3 animate-slide-up">
                    <Link
                      to="/shop"
                      data-testid="hero-shop-btn"
                      className="inline-flex items-center gap-2 rounded-sm bg-[#20a6da] px-7 py-4 text-sm font-bold uppercase tracking-[0.15em] text-[#0a0a0a] hover:bg-[#1c92bf] hover:-translate-y-0.5 transition-all"
                    >
                      Shop Now <ArrowRight size={16} />
                    </Link>
                  </div>

                  {heroBody ? (
                    <div data-testid="home-hero-markdown-body">
                      <Suspense fallback={<HeroPlain markdown={heroBody} />}>
                        <HeroMarkdown markdown={heroBody} />
                      </Suspense>
                    </div>
                  ) : null}

                  <ReviewHighlights inline />
                </>
              );
            })()}
          </div>

          <div className="lg:col-span-5 relative">
            <Link
              to="/shop"
              data-testid="hero-image-shop-link"
              aria-label="Browse the shop"
              className="block relative aspect-square overflow-hidden rounded-md bg-gradient-to-br from-[#1a1a1a] to-[#0e0e0e] border border-white/5 transition-colors hover:border-white/25"
            >
              <SafeImage
                src={toAvif(headline.image_url || IMAGES[headline.id])}
                alt={headline.name}
                loading="eager"
                decoding="async"
                fetchPriority="high"
                className="h-full w-full object-cover"
              />
              <div className="absolute top-5 left-5">
                <HeroWavingFlag />
              </div>
              <div
                data-testid="lab-select-pill"
                title="Highest purity"
                className="pulse-cyan absolute top-5 right-5 inline-flex items-center gap-2 rounded-full bg-[#20a6da] text-[#0a0a0a] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em]"
              >
                Lab Select
              </div>
            </Link>
          </div>
        </div>

        {/* Scrolling marquee */}
        <div className="border-t border-white/5 bg-[#0e0e0e] overflow-hidden">
          <div className="flex gap-12 py-4 animate-scroll-x whitespace-nowrap will-change-transform">
            {Array.from({ length: 2 }).flatMap((_, k) =>
              [
                "GLP-1 / GIP / Glucagon",
                "High quality (99%+ purity)",
                "Lyophilized product",
                "Canadian tested",
                "Traceable product batches",
                "Research use only",
              ].map((t, i) => (
                <span
                  key={`${k}-${i}`}
                  className="overline text-[#bfcde3]/70 text-[11px]"
                >
                  ‖ {t}
                </span>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Features row */}
      <section className="bg-[#0e0e0e] border-b border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 grid gap-8 md:grid-cols-2 lg:grid-cols-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="flex gap-4">
              <span className="h-11 w-11 shrink-0 rounded-sm bg-[#20a6da]/10 border border-[#20a6da]/30 inline-flex items-center justify-center text-[#20a6da]">
                <f.icon size={18} strokeWidth={2.25} />
              </span>
              <div>
                <p className="font-display text-sm uppercase tracking-[0.1em] font-[700] text-white">
                  {f.title}
                </p>
                <p className="mt-2 text-base text-[#bfcde3] leading-relaxed">
                  {f.copy}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}
